import { Link } from "react-router-dom";

import {
  companyNumber,
  companyRegistrationPlace,
  footerProductLinks,
  footerSourceLinks,
  footerSupportLinks,
  legalEntityName,
  legalLastUpdated,
  registeredOfficeAddress,
  siteBrand,
  siteName,
  type FooterLink,
} from "@consts";
import { SiteLogoMark } from "../SiteLogo";

function FooterAnchor({ link }: { link: FooterLink }) {
  const className = "hover:text-info";
  if (link.external) {
    return (
      <a href={link.to} target="_blank" rel="noopener noreferrer" className={className}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.to} className={className}>
      {link.label}
    </Link>
  );
}

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <p className="section-label mb-3">{title}</p>
      <ul className="space-y-2 text-sm text-muted">
        {links.map((link) => (
          <li key={link.to + link.label}>
            <FooterAnchor link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-line/20 bg-surface/40">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-3">
        <div>
          <Link to="/" className="group inline-flex items-center gap-3">
            <SiteLogoMark className="h-10 w-10 shrink-0 transition group-hover:scale-[1.03]" />
            <div>
              <p className="font-display text-xl font-semibold text-ink transition group-hover:text-accent">
                {siteBrand}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-accent">
                {legalEntityName}
              </p>
            </div>
          </Link>
          <p className="mt-3 max-w-sm text-sm text-muted">
            Regime intelligence for persistent extreme-weather risk — not another weather app.
          </p>
        </div>

        <FooterColumn title="Site" links={footerProductLinks} />
        <FooterColumn title="Contact" links={footerSupportLinks} />
      </div>

      <div className="border-t border-line/15">
        <details className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
          <summary className="section-label w-fit cursor-pointer marker:text-accent">
            Sources
          </summary>
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm leading-relaxed text-muted">
            {footerSourceLinks.map((link) => (
              <li key={link.to + link.label}>
                <FooterAnchor link={link} />
              </li>
            ))}
          </ul>
        </details>
      </div>

      <div className="border-t border-line/15">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 text-xs text-faint sm:px-6">
          <p>
            {legalEntityName} · Company no. {companyNumber} · Registered in{" "}
            {companyRegistrationPlace}
            <br />
            {registeredOfficeAddress}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {year} {legalEntityName}. {siteName}.
            </p>
            <p>Policies last updated {legalLastUpdated}.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
